import { find } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Inject, Injectable, OnDestroy } from '../../injection';
import { NOTHING_TO_DO, ReadonlyBehaviorSubject } from '../../rxjs-utils';

import { OrganizationMembership } from '../data/organization-membership.do';
import { User } from '../data/user.do';

import { IAuthTokenService } from './auth-token.interface';
import { IMobizStateService, LAST_SELECTED_MEMBERSHIP_KEY, LEGACY_SELECTED_MEMBERSHIP_KEY } from './mobiz-state.interface';
import { IMobizStorageService } from './mobiz-storage.interface';
import { IUserService } from './user.interface';

@Injectable()
export class MobizStateService implements OnDestroy, IMobizStateService {

  /**
   * @inheritdoc
   */
  public readonly currentMembership: ReadonlyBehaviorSubject<OrganizationMembership | null>
    = new BehaviorSubject<OrganizationMembership | null>(null);

  /**
   * @inheritdoc
   */
  public readonly currentUser: ReadonlyBehaviorSubject<User | null>
    = new BehaviorSubject<User | null>(null);

  /**
   * @inheritdoc
   */
  public get initialized(): boolean {
    return this._initialized;
  }
  private _initialized: boolean = false;

  constructor(
    @Inject(IAuthTokenService)
    private authTokenService: IAuthTokenService,
    @Inject(IMobizStorageService)
    private storageService: IMobizStorageService,
    @Inject(IUserService)
    private userService: IUserService,
  ) {
  }

  public ngOnDestroy(): void {
    this.currentUser.complete();
    this.currentMembership.complete();
  }

  /**
   * @inheritdoc
   */
  public init(): Observable<void> {
    const disableErrorHandling: boolean = true;
    return this.userService.getUser(this.getCurrentUserId(), disableErrorHandling).pipe(
      map((currentUser: User): void => {
        this.changeCurrentUser(currentUser);
        const currentMembership: OrganizationMembership | null = this.getLastSelectedMembership(currentUser);
        this.changeCurrentMembership(currentMembership);
        this._initialized = true;
        return;
      }),
    );
  }

  public changeCurrentMembership(currentMembership: OrganizationMembership | null): void {
    (this.currentMembership as BehaviorSubject<OrganizationMembership | null>).next(currentMembership);
    if (currentMembership) {
      this.storageService.set(LAST_SELECTED_MEMBERSHIP_KEY, currentMembership.id);
    } else {
      this.storageService.remove(LAST_SELECTED_MEMBERSHIP_KEY);
    }
    // TODO: #7144 Remove this BC layer.
    // Cleanup legacy value membership value that is still used by the 'old-web'.
    this.storageService.remove(LEGACY_SELECTED_MEMBERSHIP_KEY);
  }

  /**
   * @inheritdoc
   */
  public refresh(): Observable<void> {
    this._initialized = false;
    return this.init();
  }

  /**
   * @inheritdoc
   */
  public refreshInBackground(): void {
    this.refresh().subscribe(NOTHING_TO_DO);
  }

  private changeCurrentUser(currentUser: User): void {
    (this.currentUser as BehaviorSubject<User>).next(currentUser);
  }

  /**
   * Internal helper to load the current membership data.
   */
  private getLastSelectedMembership(currentUser: User): OrganizationMembership | null {
    // Load the membership information from the User data.
    const lastSelectedMembershipId: string | null = this.getLastSelectedMembershipId();
    if (!lastSelectedMembershipId) {
      return null;
    }
    const membership: OrganizationMembership | undefined = find(
      currentUser.membership,
      { id: lastSelectedMembershipId }
    );
    return membership ? membership : null;
  }

  /**
   * Helper function to read the currently selected OrganizationMembership ID from storage.
   */
  private getLastSelectedMembershipId(): string | null {
    // In order to support refreshing of the cached data, we return the currently selected ID.
    if (this.currentMembership.value) {
      return this.currentMembership.value.id;
    }
    const lastSelectedMembershipId: string | null = this.storageService.get<string>(LAST_SELECTED_MEMBERSHIP_KEY);
    if (lastSelectedMembershipId) {
      return lastSelectedMembershipId;
    }
    // TODO: #7144 Remove this BC layer.
    // Support users with old storage data and update the data.
    const membership: OrganizationMembership | null = this.storageService.get<OrganizationMembership>(LEGACY_SELECTED_MEMBERSHIP_KEY);
    if (membership) {
      this.storageService.set(LAST_SELECTED_MEMBERSHIP_KEY, membership.id);
      this.storageService.remove(LEGACY_SELECTED_MEMBERSHIP_KEY);
      return membership.id;
    }
    return null;
  }

  /**
   * Helper function to read the currently logged in User ID from storage.
   */
  private getCurrentUserId(): string {
    return this.authTokenService.session.getClaimsForAccessToken().user_id;
  }
}
